// Design frames: hifi/06-visit-inbox.html (H6.2), wireframes/16-visits-m3.html (16.3 to 16.16).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ActionBar,
  ArchThumb,
  Avatar,
  Button,
  Card,
  Icon,
  IconButton,
  List,
  ListItem,
  Menu,
  Notice,
  Skeleton,
  StateScreen,
  iconChevron,
  iconMore,
} from '@sauda/ui/kit';
import { LinkButton } from '../../app/LinkButton.js';
import { Screen, useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { initials } from '../../lib/initials.js';
import { formatRupees } from '../../lib/money.js';
import { formatPhone } from '../../lib/phone.js';
import { whatsappLink } from '../../lib/share-text.js';
import { formatIst } from '../../lib/time.js';
import type { VisitView } from '../../lib/types.js';
import { useVisitActions } from './useVisitActions.js';
import { VisitTimeSheet } from './VisitTimeSheet.js';

/**
 * One visit: the slot in large type, who is coming, the property, and the actions its status
 * allows. A masked number shows no call or WhatsApp button, because the role may not use it.
 */
export function VisitScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const auth = useAuth();
  const chrome = useChrome();
  const navigate = useNavigate();
  const { act, startDeal, error, withUndo } = useVisitActions();
  const [timing, setTiming] = useState<'confirm' | 'move' | null>(null);
  const visit = useQuery({
    queryKey: ['visit', id],
    queryFn: () => api<VisitView>(`/visits/${id}`),
  });
  const v = visit.data;
  if (visit.isError)
    return (
      <Screen title={t('visits.detail')} back="/visits">
        <StateScreen kind="empty" compact title={t('visits.notFound')} />
      </Screen>
    );
  if (v === undefined)
    return (
      <Screen title={t('visits.detail')} back="/visits">
        <Skeleton height={120} />
      </Screen>
    );

  const mine = v.assigneeMemberId === auth.current?.memberId;
  const open = ['requested', 'confirmed'].includes(v.status);
  const canAct = !chrome.readOnly && (auth.current?.role !== 'agent' || mine) && open;
  const moved = v.history.at(-1);

  // "Sun 19 Oct, 4 to 6 pm" reads as a date in large type and a time beneath it.
  const comma = v.slot.indexOf(',');
  const callMe = v.slot === 'call_me';
  const day = callMe ? t('visits.callMe') : comma < 0 ? v.slot : v.slot.slice(0, comma);
  const hour = callMe || comma < 0 ? null : v.slot.slice(comma + 1).trim();
  const menu = canAct ? (
    <Menu
      label={t('visits.more')}
      items={[
        { key: 'move', label: t('visits.reschedule'), onSelect: () => setTiming('move') },
        {
          key: 'cancel',
          label: t('visits.cancel'),
          destructive: true,
          onSelect: () => {
            withUndo(v.id, 'cancel', t('visits.cancelled'));
            void navigate('/visits');
          },
        },
      ]}
      trigger={(props) => <IconButton {...props} label={t('visits.more')} icon={iconMore} />}
    />
  ) : undefined;

  return (
    <Screen title={t('visits.detail')} back="/visits" actions={menu}>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Card hero>
        <p className="k-t-label-sm">{t('visits.detail')}</p>
        <p className="k-t-display-sm hero-number">{day}</p>
        <p className="k-t-body">
          {[hour, t(`visits.status_${v.status}`)].filter(Boolean).join(' · ')}
        </p>
      </Card>
      <Card>
        <div className="deal-client__row">
          <Avatar initials={initials(v.client.name)} />
          <div className="row-main k-grow">
            <h2 className="k-t-title-sm">{v.client.name}</h2>
            <p className="k-t-body k-muted" data-testid="client-phone">
              {formatPhone(v.client.phone)}
            </p>
          </div>
        </div>
        {v.client.phoneMasked ? (
          <p className="k-t-body-sm k-muted">{t('visits.phoneMasked')}</p>
        ) : (
          <div className="chips-row">
            <LinkButton variant="tonal" href={`tel:${v.client.phone}`}>
              {t('visits.call')}
            </LinkButton>
            <LinkButton variant="tonal" href={whatsappLink('', v.client.phone)}>
              {t('visits.whatsapp')}
            </LinkButton>
          </div>
        )}
      </Card>
      <List label={t('visits.property')}>
        <ListItem
          title={v.propertyTitle}
          leading={<ArchThumb seed={v.propertyId} />}
          supporting={[
            v.propertyAmountPaise === null ? null : formatRupees(v.propertyAmountPaise),
            v.propertyLocality,
          ]
            .filter(Boolean)
            .join(' · ')}
          trailing={<Icon icon={iconChevron} />}
          onClick={() => void navigate(`/properties/${v.propertyId}`)}
        />
      </List>
      <p className="k-t-label-sm k-muted">
        {moved === undefined
          ? t('visits.booked', { time: formatIst(v.createdAt) })
          : t(moved.byName === null ? 'visits.bookedMovedNoName' : 'visits.bookedMoved', {
              time: formatIst(v.createdAt),
              from: moved.slot === 'call_me' ? t('visits.callMe') : moved.slot,
              by: moved.byName,
            })}
      </p>
      <div className="chips-row">
        <LinkButton variant="text" to={`/introductions/${v.introductionId}`}>
          {t('visits.proof')}
        </LinkButton>
        {auth.allowed('deals.run') && v.status === 'done' ? (
          <Button
            size="lg"
            loading={startDeal.isPending}
            disabled={chrome.readOnly}
            onClick={() => startDeal.mutate(v.id)}
          >
            {t('visits.startDeal')}
          </Button>
        ) : null}
      </div>
      {chrome.readOnly && open ? (
        <p className="k-t-body-sm k-muted">{t('deal.readOnlyReason')}</p>
      ) : null}
      {canAct ? (
        <ActionBar label={t('visits.finish')}>
          {v.status === 'requested' ? (
            <>
              <Button variant="tonal" size="lg" onClick={() => setTiming('confirm')}>
                {t('visits.confirmWithTime')}
              </Button>
              <Button
                size="lg"
                loading={act.isPending}
                onClick={() => act.mutate({ id: v.id, action: 'confirm' })}
              >
                {t('visits.confirm')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="tonal"
                size="lg"
                onClick={() => {
                  withUndo(v.id, 'no-show', t('visits.noShowMarked'));
                  void navigate('/visits');
                }}
              >
                {t('visits.noShow')}
              </Button>
              <Button
                size="lg"
                loading={act.isPending}
                onClick={() => act.mutate({ id: v.id, action: 'done' })}
              >
                {t('visits.done')}
              </Button>
            </>
          )}
        </ActionBar>
      ) : null}
      <VisitTimeSheet
        open={timing !== null}
        onClose={() => setTiming(null)}
        title={timing === 'confirm' ? t('visits.confirmWithTime') : t('visits.reschedule')}
        actionLabel={timing === 'confirm' ? t('visits.confirm') : t('visits.reschedule')}
        pending={act.isPending}
        onSubmit={(at) =>
          act.mutate(
            { id: v.id, action: timing === 'confirm' ? 'confirm' : 'reschedule', body: { at } },
            { onSuccess: () => setTiming(null) },
          )
        }
      />
    </Screen>
  );
}
